import { createSlice } from "@reduxjs/toolkit";
import { isAxiosError } from "axios";
import { PostPublishStatus, Publish, ServerError } from "../types/index.js";
import { fetch } from "../utils/utils.js";
import { showAlert } from "./alerts.js";
import { createAppAsyncThunk } from "./utils.js";

const sliceName = "publish";

const initialState: Publish = { status: "ready" };

export const getPublishStatus = createAppAsyncThunk(
  `${sliceName}/getPublishStatus`,
  async (
    { siteId, showLastResult }: { siteId: string; showLastResult: boolean },
    { dispatch },
  ) => {
    const { data } = await fetch({ dispatch }).get<Publish>(
      `sites/${siteId}/publish`,
      { params: { showLastResult } },
    );
    return data;
  },
);

export const postPublish = createAppAsyncThunk<
  { data: Publish; result: PostPublishStatus },
  string,
  { rejectValue: PostPublishStatus }
>(
  `${sliceName}/postPublish`,
  async (siteId: string, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await fetch({ dispatch }).post<Publish>(
        `sites/${siteId}/publish`,
      );
      return { data, result: "success" };
    } catch (error) {
      const isCriticalIssuesError =
        isAxiosError<ServerError>(error) &&
        error.response?.data.error === "critical errors found";

      if (isCriticalIssuesError) return rejectWithValue("criticalIssues");

      dispatch(
        showAlert(
          "Beim Veröffentlichen der Seite ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut, oder wenden Sie sich an den Support!",
        ),
      );
      throw error;
    }
  },
);

const slice = createSlice({
  name: sliceName,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      getPublishStatus.fulfilled,
      (_state, action) => action.payload,
    );
    builder.addCase(postPublish.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(postPublish.rejected, (state) => {
      state.status = "failed";
    });
    builder.addCase(
      postPublish.fulfilled,
      (_state, action) => action.payload.data,
    );
  },
});

export default slice.reducer;
